// hooks/use-driver-details.ts
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch } from '../../../Redux/store';
import useVehicleTypes from '../../useVehicleTypes';
import { useGetDriverDetails } from '../../../apis/drivers/get-driver-details';
import { useModifyDriver } from '../../../apis/drivers/modify-driver';
import { useGetDriverAddress } from '../../../apis/drivers/get-driver-address';
import { decrementCount } from '../../../Redux/slices/ui';
import { VERIFICATION_STATUS } from '../../../utils/Enums';
import { useGetDriverAnalysis } from '../../../apis/drivers/use-get-driver-cancellation-rate';

type StatusType = "approved" | "rejected";

const useDriverDetails = () => {
    const { id } = useParams();
    const dispatch = useAppDispatch();
    const { getVehicleLabel } = useVehicleTypes();

    const [isMessageVisible, setMessageVisible] = useState(false);
    const [isPayoutVisible, setPayoutVisible] = useState(false);
    const [isViewingResubmittedDocuments, setIsViewingResubmittedDocuments] = useState(false);
    const [isRejectReasonModalVisible, setIsRejectReasonModalVisible] = useState(false);
    const [driverAddress, setDriverAddress] = useState<string | null>(null);
    const [image, setImage] = useState<string | null>(null);
    const [zoomed, setZoomed] = useState(false);

    const { data: details, isFetching: loading, refetch: refetchDriverDetails } = useGetDriverDetails(id);
    const { mutateAsync: modifyDriver, isPending: isModifying } = useModifyDriver();
    const { refetch: fetchAddress, isFetching: isGettingAddress } = useGetDriverAddress(id || "");
    const { data: driverAnalysis } = useGetDriverAnalysis({ id: id || "" });

    const handleZoomImage = (src: string) => {
        setImage(src);
        setZoomed(true);
    };

    const changeStatus = async (value: StatusType, reason?: string) => {
        if (!details?.isEmailVerified) {
            setMessageVisible(true);
            return;
        }

        const message = {
            approved: "Driver approved successfully",
            rejected: "Driver rejected successfully",
        };

        const body: { verificationStatus: StatusType; rejectReason?: string } = {
            verificationStatus: value,
            ...(value === "rejected" && reason ? { rejectReason: reason } : {}),
        };

        await modifyDriver({ id: id || "", body });
        refetchDriverDetails();
        toast.success(message[value]);
        dispatch(decrementCount());
        setIsRejectReasonModalVisible(false);
    };

    const handleGetDriverAddress = async () => {
        const res = await fetchAddress();
        if (res?.data) {
            setDriverAddress(res.data.address);
        } else {
            toast.error("Failed to get driver address");
        }
    };

    const isPendingReview = details?.verificationStatus === VERIFICATION_STATUS.PENDING_REVIEW;
    const isResubmit = details?.verificationStatus === VERIFICATION_STATUS.RESUBMIT;

    return {
        id,
        details,
        loading,
        isModifying,
        isGettingAddress,
        isMessageVisible,
        setMessageVisible,
        isPayoutVisible,
        setPayoutVisible,
        isViewingResubmittedDocuments,
        setIsViewingResubmittedDocuments,
        isRejectReasonModalVisible,
        setIsRejectReasonModalVisible,
        driverAddress,
        image,
        zoomed,
        setZoomed,
        getVehicleLabel,
        handleZoomImage,
        changeStatus,
        handleGetDriverAddress,
        refetchDriverDetails,
        isPendingReview,
        isResubmit,
        driverAnalysis
    };
};

export default useDriverDetails;